import { inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ClearCacheService } from "@shared/services/clear-cache/clear-cache.service";

export type CacheQueryResult<T, Args extends unknown[] = unknown[]> = {
    fetch: (...args: Args) => Promise<T>;
    clearCache: (...args: Args) => void;
    clearAllCache: () => void;
    isCacheValid: (...args: Args) => boolean;
};

// Creates an in-memory cache for async query functions with Time-To-Live (TTL) expiration,
// automatic request deduplication for concurrent calls, and automatic invalidation upon user logout.
export function createQueryCache<T, Args extends unknown[] = unknown[]>(
    fetchFn: (...args: Args) => Promise<T>,
    ttlMilliseconds: number = 3 * 60 * 1000,
): CacheQueryResult<T, Args> {
    // Cache data, timestamps, and in-flight requests separately.
    const dataCache: Map<string, T> = new Map<string, T>();
    const cacheTimestamp: Map<string, number> = new Map<string, number>();
    const pendingPromise: Map<string, Promise<T>> = new Map<string, Promise<T>>();

    const clearCacheService: ClearCacheService = inject(ClearCacheService);

    clearCacheService.logout$.pipe(takeUntilDestroyed()).subscribe(() => {
        dataCache.clear();
        cacheTimestamp.clear();
        pendingPromise.clear();
    });

    return {
        fetch: (...args: Args): Promise<T> => {
            const cacheKey: string = JSON.stringify(args);
            const cached: T | undefined = dataCache.get(cacheKey);
            const timestamp: number | undefined = cacheTimestamp.get(cacheKey);
            const promise: Promise<T> | undefined = pendingPromise.get(cacheKey);

            // Return a cloned cached value while it is still fresh.
            if (cached !== undefined && timestamp !== undefined && Date.now() - timestamp < ttlMilliseconds) {
                return Promise.resolve(cloneCacheValue(cached));
            }

            // Reuse the in-flight request so concurrent callers share one fetch.
            if (promise !== undefined) {
                return promise.then((response: T) => cloneCacheValue(response));
            }

            const fetchPromise: Promise<T> = fetchFn(...args)
                .then((response: T) => {
                    const cachedResponse: T = cloneCacheValue(response);

                    // Store a cloned copy so future callers cannot mutate the cached object.
                    dataCache.set(cacheKey, cachedResponse);
                    cacheTimestamp.set(cacheKey, Date.now());

                    return response;
                })
                .finally(() => {
                    pendingPromise.delete(cacheKey);
                });

            pendingPromise.set(cacheKey, fetchPromise);

            return fetchPromise.then((response: T) => cloneCacheValue(response));
        },

        clearCache: (...args: Args) => {
            const cacheKey: string = JSON.stringify(args);

            dataCache.delete(cacheKey);
            cacheTimestamp.delete(cacheKey);
            pendingPromise.delete(cacheKey);
        },

        clearAllCache: () => {
            dataCache.clear();
            cacheTimestamp.clear();
            pendingPromise.clear();
        },

        isCacheValid: (...args: Args): boolean => {
            const cacheKey: string = JSON.stringify(args);
            const cached: T | undefined = dataCache.get(cacheKey);
            const timestamp: number | undefined = cacheTimestamp.get(cacheKey);

            return cached !== undefined && timestamp !== undefined && Date.now() - timestamp < ttlMilliseconds;
        },
    };
}

function cloneCacheValue<T>(value: T): T {
    // Use structuredClone when available, otherwise fall back to JSON.parse(JSON.stringify(value)).
    if (typeof structuredClone === "function") {
        return structuredClone(value);
    }

    return JSON.parse(JSON.stringify(value));
}
